import React, { Dispatch, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { MenuItem, Select, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    getDocumentActivityLogs,
    getDocumentActivityLogsConfig,
    IActivityLogConfig,
    IActivityLogs,
    ProjectSidepanel,
    setActiveDocument,
} from '../../../redux/project/sliceProjectSidepanel';
import { IAppReduxStore } from '../../../redux/store';
import DocumentActivityLogs from './Components/DocumentActivityLogs';
import AssignmentActivityGroups from './Components/AssignmentActivityGroups';
import WorkflowActivityLogs from './Components/WorkflowActivityLogs';
import PlaceholderCard from './Components/PlaceholderCard';
import LoadingData from './Components/LoadingData';
import { findProjectDocument } from '../../../util/DocUtils';

enum ActivityLogType {
    activity = 'activity',
    assignments = 'assignments',
    workflow = 'workflow',
}

const stackSx = { pl: 2, pr: 1, py: 1, overflow: 'hidden' };

export default () => {
    const { replicaId, areaName } = useParams();
    const { t } = useTranslation('translations', { keyPrefix: 'sidepanel' });
    const dispatch: Dispatch<unknown> = useDispatch();

    const { data, loading } = useSelector((store: IAppReduxStore) => store.projectSidepanel);
    const universalId = useSelector((store: IAppReduxStore) => store.projectSidepanel.targetDocId);
    const [selected, setSelected] = useState<ActivityLogType>(ActivityLogType.activity);

    useEffect(() => {
        if (!data?.[ProjectSidepanel.Activities]?.['config']) {
            dispatch(getDocumentActivityLogsConfig());
        }
    }, []);

    useEffect(() => {
        if (universalId) {
            dispatch(getDocumentActivityLogs({ replicaId, universalId }));
        }
    }, [universalId]);

    const displayData = useMemo(
        () => (data?.[ProjectSidepanel.Activities]?.['logs'] ?? {}) as IActivityLogs,
        [data],
    );

    const doc = useMemo(() => findProjectDocument(universalId, areaName), [universalId, areaName]);

    const indices = useMemo(() => displayData?.indices ?? [], [displayData]);

    const displayConfig = useMemo(
        () => (data?.[ProjectSidepanel.Activities]?.['config'] ?? []) as IActivityLogConfig[],
        [data],
    );

    const activityLogTabs = useMemo(() => {
        return {
            [ActivityLogType.activity]: (
                <DocumentActivityLogs data={displayData.activity} config={displayConfig} />
            ),
            [ActivityLogType.assignments]: (
                <AssignmentActivityGroups data={displayData.assignments} />
            ),
            [ActivityLogType.workflow]: <WorkflowActivityLogs data={displayData.workflow} />,
        };
    }, [displayData]);

    const hasData = useMemo(() => displayData?.[selected]?.length > 0, [displayData, selected]);

    const onIndexSelectionChange = (ev: SelectChangeEvent<string>) => {
        // ev.target.value = UniversalID
        const newSelectedDoc = findProjectDocument(ev.target.value, areaName);
        dispatch(
            setActiveDocument({
                UniversalId: newSelectedDoc.UniversalID,
                NoteId: newSelectedDoc.NoteId,
            }),
        );
    };

    return (
        <Stack spacing={1} sx={stackSx}>
            <Typography
                variant='h6'
                sx={{
                    fontSize: 16,
                    py: 1,
                }}
            >
                {t('activities')}
            </Typography>
            <Stack direction='row' sx={{ justifyContent: "space-between" }}>
                <Select
                    value={selected}
                    onChange={e => setSelected(e.target.value as ActivityLogType)}
                    variant='standard'
                    disableUnderline
                >
                    {Object.keys(ActivityLogType).map(option => (
                        <MenuItem key={option} value={option}>
                            {t(option)}
                        </MenuItem>
                    ))}
                </Select>
                {indices.length > 1 ? (
                    <Select
                        value={doc?.UniversalID ?? ''}
                        onChange={onIndexSelectionChange}
                        variant='standard'
                        disableUnderline
                    >
                        {indices.map(index => (
                            <MenuItem key={index.UniversalID} value={index.UniversalID}>
                                {index.Index}
                            </MenuItem>
                        ))}
                    </Select>
                ) : (
                    indices.length === 1 && (
                        <Typography
                            sx={{
                                display: 'flex',
                                justifyContent: 'end',
                                width: '100%',
                                marginTop: '4px',
                                marginRight: '10px',
                            }}
                        >
                            {`${t('index')}: ${indices[0].Index}`}
                        </Typography>
                    )
                )}
            </Stack>
            {activityLogTabs[selected]}
            {loading && <LoadingData />}
            {!loading && !hasData && <PlaceholderCard translationKey='NoActivitiesPlaceholder' />}
        </Stack>
    );
};
